import { z } from "zod";

/**
 * `npx pagerless connect`: the CLI's device sign-in and what it reports back.
 *
 * Flow (OAuth 2.0 device grant, RFC 8628 shapes in plain JSON):
 * 1. CLI → POST /v1/cli/authorize            → CliAuthorizeStart (userCode, verifyUrl, pollToken)
 * 2. Owner opens verifyUrl, checks the code matches, confirms with their passkey.
 * 3. CLI → POST /v1/cli/token { pollToken }  → CliTokenPoll until "approved" (once; then "used")
 * 4. CLI uses `Bearer plc_…` (short-lived, connect-only scope) for projects, keys, connections,
 *    capabilities and the final report.
 */

/** Bearer prefix of the short-lived token a CLI gets after approval. */
export const CLI_TOKEN_PREFIX = "plc_";
/** Prefix of the secret the CLI polls with (never shown to a person). */
export const CLI_POLL_PREFIX = "plp_";

export const CliTiming = {
  /** How long the code on screen stays valid. */
  codeTtlS: 600,
  /** Minimum seconds between polls; faster polls get "slow_down". */
  pollIntervalS: 3,
  /** Lifetime of the CLI token. Long enough to apply a plan, short enough to be worthless later. */
  tokenTtlS: 3600,
} as const;

/** 8 letters from an alphabet with no vowels or look-alikes, shown as XXXX-XXXX. */
export const CLI_CODE_ALPHABET = "BCDFGHJKLMNPQRSTVWXZ";
export const CliUserCode = z
  .string()
  .trim()
  .toUpperCase()
  .transform((s) => s.replace(/[\s-]/g, ""))
  .pipe(z.string().regex(/^[BCDFGHJKLMNPQRSTVWXZ]{8}$/));

/** Formats a stored code for people: "BCDF-GHJK". */
export function formatUserCode(code: string): string {
  return `${code.slice(0, 4)}-${code.slice(4, 8)}`;
}

const Label = z
  .string()
  .trim()
  .max(100)
  .regex(/^[^\p{Cc}<>]*$/u);

export const CliAuthorizeBody = z.object({
  client: z.object({ name: z.literal("pagerless-cli"), version: z.string().max(40) }),
  /** The machine's hostname, so the owner can tell their laptop from someone else's. */
  host: Label.nullable().default(null),
  /** The repo being connected (its folder name), shown on the approval page. */
  repo: Label.nullable().default(null),
});
export type CliAuthorizeBody = z.infer<typeof CliAuthorizeBody>;

export const CliAuthorizeStart = z.object({
  userCode: z.string(),
  verifyUrl: z.string().url(),
  pollToken: z.string().startsWith(CLI_POLL_PREFIX),
  interval: z.number().int().positive(),
  expiresIn: z.number().int().positive(),
});
export type CliAuthorizeStart = z.infer<typeof CliAuthorizeStart>;

export const CliGrantState = z.enum(["pending", "approved", "denied", "used", "expired"]);
export type CliGrantState = z.infer<typeof CliGrantState>;

/** What the approval page shows about a waiting CLI. */
export const CliGrantView = z.object({
  userCode: z.string(),
  host: z.string().nullable(),
  repo: z.string().nullable(),
  client: z.string(),
  state: CliGrantState,
  createdAt: z.number().int(),
  expiresAt: z.number().int(),
});
export type CliGrantView = z.infer<typeof CliGrantView>;

export const CliTokenBody = z.object({ pollToken: z.string().startsWith(CLI_POLL_PREFIX).max(100) });

export const CliTokenPoll = z.discriminatedUnion("status", [
  z.object({ status: z.literal("pending") }),
  z.object({ status: z.literal("slow_down"), interval: z.number().int().positive() }),
  z.object({ status: z.literal("denied") }),
  z.object({ status: z.literal("expired") }),
  /** The token was already handed out once; a second poll never gets it. */
  z.object({ status: z.literal("used") }),
  z.object({
    status: z.literal("approved"),
    token: z.string().startsWith(CLI_TOKEN_PREFIX),
    expiresAt: z.number().int(),
    account: z.object({ id: z.string(), name: z.string() }),
  }),
]);
export type CliTokenPoll = z.infer<typeof CliTokenPoll>;

/** What this server tells `connect`. */
export const CliCapabilities = z.object({
  /** Where an app sends events, when it is not the package default (Ingest.baseUrl + eventsPath). */
  eventsUrl: z.string().url().nullable(),
  /**
   * Where SNS topics deliver CloudWatch alarms (ingest's /v1/aws/sns). The CLI adds the ingest key as
   * basic auth (`https://pagerless:<key>@host/v1/aws/sns`) when it subscribes, so the key never sits in a file.
   */
  snsEndpoint: z.string().url(),
  /** AWS undo access: a CloudFormation template that creates a role Pagerless assumes with this ExternalId. */
  awsRole: z
    .object({ templateUrl: z.string().url(), externalId: z.string().min(16).max(128), principalArn: z.string() })
    .nullable(),
});
export type CliCapabilities = z.infer<typeof CliCapabilities>;

export const CliRuntime = z.enum(["node", "go", "cloudflare", "python", "aws"]);
export type CliRuntime = z.infer<typeof CliRuntime>;

export const CliDeploy = z.object({
  kind: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9-]+$/),
  names: z
    .array(Label.pipe(z.string().min(1)))
    .max(20)
    .default([]),
});
export type CliDeploy = z.infer<typeof CliDeploy>;

/** The CLI's summary after applying a plan. The setup screen shows it. Names only, never keys. */
export const CliReport = z.object({
  projectId: z.string().min(1).max(64),
  units: z
    .array(
      z.object({
        name: Label.pipe(z.string().min(1)),
        runtime: CliRuntime,
        framework: Label.nullable(),
        /**
         * Additive: where it is deployed. `kind` is the CLI's deploy kind ("ecs", "lambda",
         * "cloudflare-worker", "cloudflare-pages", "vercel", "railway", "local", ...); `names` are the
         * names it has there (ECS service, Lambda functions, Worker name), so signals that arrive under
         * those names (an ECS task stopped in "proofsource-api-api") belong to this unit.
         */
        deploy: CliDeploy.optional(),
      }),
    )
    .max(100),
  services: z.number().int().min(0).max(1000),
  alarms: z.number().int().min(0).max(100_000),
  jobs: z.number().int().min(0).max(10_000),
});
export type CliReport = z.infer<typeof CliReport>;

export const CliReportView = CliReport.extend({ at: z.number().int(), repo: z.string().nullable() });
export type CliReportView = z.infer<typeof CliReportView>;

/** "Connected 4 services · 117 alarms · 30 jobs": same words in the terminal and on the setup screen. */
export function connectedLine(r: Pick<CliReport, "services" | "alarms" | "jobs">): string {
  const n = (k: number, one: string) => `${k} ${one}${k === 1 ? "" : "s"}`;
  return `Connected ${[n(r.services, "service"), n(r.alarms, "alarm"), n(r.jobs, "job")].join(" · ")}`;
}
